import Constants from '@/Constants';
import { usePrevious } from '@/lib/hooks';
import { ContentPaginationData } from '@/models/ContentPaginationData';
import { ContentRequest } from '@/models/ContentRequest';
import { ContentSectionConfiguration } from '@/models/ContentSectionConfiguration';
import { ViewType } from '@/models/ViewType';
import { analyticsService, PlaylistEvents } from '@/services/AnalyticsService';
import { contentService } from '@/services/ContentService';
import { courseService } from '@/services/CourseService';
import { ApplicationState } from '@/store';
import { getContentListRequest } from '@/store/item/action';
import { ContentStudentView, ItemTypes } from '@/store/item/types';
import React, { useCallback, useEffect, useState } from 'react';
import { useBottomScrollListener } from 'react-bottom-scroll-listener';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import ContentCard from '../ContentCard';
import Loader from '../Loader';
import './style.scss';

interface Props {
    viewType: ViewType;
    limit: number;
    simplified: boolean;
    randomizeRecommendations?: boolean;
    localesToFilter?: string;
}

const getEventNameByType = (type: string, categoryId?: string) => {
    if (type.startsWith(Constants.listCategorizedPrefix) && categoryId) {
        return PlaylistEvents.ACCESS_ITEM_SECTION_IN_CATEGORY.replace('%n', categoryId);
    }

    switch (type) {
        case 'listLastPublishedStudentView':
            return PlaylistEvents.ACCESS_ITEM_SECTION_LAST_PUBLISHED;
        case 'listMostAccessedStudentView':
            return PlaylistEvents.ACCESS_ITEM_SECTION_MOST_ACCESSED;
        case 'listInProgressStudentView':
            return PlaylistEvents.ACCESS_ITEM_SECTION_IN_PROGRESS;
        case 'listCompletedStudentView':
            return PlaylistEvents.ACCESS_ITEM_SECTION_MY_COMPLETED;
        case 'listTopRatedStudentView':
            return PlaylistEvents.ACCESS_ITEM_SECTION_TOP_RATED;
        case 'listFavoriteStudentView':
            return PlaylistEvents.ACCESS_ITEM_SECTION_MY_FAVORITES;
        case 'listLikeYouStudentView':
            return PlaylistEvents.ACCESS_ITEM_SECTION_LIKE_YOU;
        case 'listForYouStudentView':
            return PlaylistEvents.ACCESS_ITEM_SECTION_FOR_YOU;
        case 'listSearchResultStudentView':
            return PlaylistEvents.ACCESS_ITEM_SECTION_SEARCH_RESULT;
        default:
            return PlaylistEvents.ACCESS_ITEM;
    }
};

const logEvent = (entityId: number, entityName: string, type: string, categoryId?: string) => {
    analyticsService.logEvent(getEventNameByType(type, categoryId), {
        entityId,
        entityName,
        entityType: Constants.entityTypes.CONTENT,
    });
};

const ContentSection: React.FC<Props> = (props: Props) => {
    const { viewType, limit, simplified, localesToFilter } = props;
    const { id: categoryId } = useParams<{ id?: string }>();
    const { id: personId } = useSelector((state: ApplicationState) => state.identity.data.person);
    const { listStudentView, listSize, listSizeTotal, favoriteContentList, favoriteLoading, contentLoading } = useSelector(
        (state: ApplicationState) => {
            const contentStudentViewList = state.itens.listData[viewType];
            return {
                favoriteContentList: courseService.getFavoriteEntityListAsIdsString(state.favorite.favoriteList, Constants.entityTypes.CONTENT),
                listStudentView: contentStudentViewList.list,
                listSize: contentStudentViewList.list.length,
                listSizeTotal: contentStudentViewList.total,
                favoriteLoading: state.favorite.loading,
                contentLoading: state.itens.loading,
            };
        },
    );
    const { term: searchTerm } = useSelector((state: ApplicationState) => state.search.data);

    const [list, setList] = useState([] as ContentStudentView[]);
    const [showLoader, setShowLoader] = useState(false);

    const prevFavoriteContentList = usePrevious(favoriteContentList);

    const dispatch = useDispatch();

    const isFavoritesPage = (): boolean => {
        return viewType === 'listFavoriteStudentView';
    };

    const isListSizeRefresh = (): boolean => {
        return !simplified && Constants.CONTENT_LIMIT > listSize && listSizeTotal > listSize;
    };

    const getContentConfiguration = (pageOffset?: number): ContentSectionConfiguration => {
        let auxViewType!: ViewType;

        if (viewType.startsWith(Constants.listCategorizedPrefix)) {
            auxViewType = 'listOfCategories';
        }

        const { type, orderBy, contentEnrollmentStatus, contentIds, doRequest, listTypeParm, term, locales } = {
            listLastPublishedStudentView: {
                type: ItemTypes.LAST_PUBLISHED_SUCCESS,
                orderBy: 'con.statusDate desc, con.views desc',
                doRequest: true,
                listTypeParm: 'NEW_RELEASES',
            },
            listMostAccessedStudentView: {
                type: ItemTypes.MOST_ACCESSED_SUCCESS,
                orderBy: 'con.views desc',
                doRequest: true,
                listTypeParm: 'MOST_ACCESSED',
            },
            listInProgressStudentView: {
                type: ItemTypes.IN_PROGRESS_SUCCESS,
                orderBy: 'cone.lastAccess desc',
                contentEnrollmentStatus: Constants.contentEnrollmentStatus.INCOMPLETE,
                doRequest: true,
                listTypeParm: 'IN_PROGRESS',
            },
            listCompletedStudentView: {
                type: ItemTypes.COMPLETED_SUCCESS,
                orderBy: 'cone.lastAccess desc',
                contentEnrollmentStatus: `${Constants.contentEnrollmentStatus.COMPLETED},${Constants.contentEnrollmentStatus.PASSED},${Constants.contentEnrollmentStatus.FAILED}`,
                doRequest: true,
                listTypeParm: 'COMPLETED',
            },
            listTopRatedStudentView: {
                type: ItemTypes.TOP_RATED_SUCCESS,
                orderBy: 'con.rating desc, con.views desc',
                doRequest: true,
                listTypeParm: 'TOP_RATED',
            },
            listLikeYouStudentView: {
                type: ItemTypes.LIKE_YOU_SUCCESS,
                orderBy: 'con.views desc',
                doRequest: true,
                listTypeParm: 'LIKE_YOU',
            },
            listFavoriteStudentView: {
                type: ItemTypes.FAVORITES_SUCCESS,
                orderBy: 'con.views desc',
                contentIds: favoriteContentList,
                doRequest: favoriteContentList.length > 0,
                listTypeParm: 'TOP_RATED',
            },
            listOfCategories: {
                type: ItemTypes.CATEGORIZED_SUCCESS,
                orderBy: 'con.views desc',
                categoryId,
                doRequest: true,
                listTypeParm: 'CATEGORIES',
            },
            listForYouStudentView: {
                type: ItemTypes.FOR_YOU_SUCCESS,
                orderBy: 'con.views desc',
                doRequest: true,
                listTypeParm: 'FOR_YOU',
            },
            listSearchResultStudentView: {
                type: ItemTypes.SEARCHED_SUCCESS,
                listTypeParm: 'SEARCH',
                term: searchTerm,
                orderBy: '',
                doRequest: true,
                locales: localesToFilter,
            },
            listMyCourseStudentView: {
                type: '',
                listTypeParm: '',
                orderBy: '',
                doRequest: false,
            },
            listSuggestedStudentView: {
                type: '',
            },
            listEssentialStudentView: {
                type: ItemTypes.ESSENTIALS_SUCCESS,
                doRequest: false,
            },
            listTrainingsEnrolledStudentView: {
                type: ItemTypes.TRAININGS_SUCCESS,
                doRequest: false,
            },
        }[auxViewType || viewType];

        let paginationData: ContentPaginationData = {
            offset: pageOffset !== undefined ? pageOffset : listSize,
            limit,
            orderBy,
            doRequest,
            contentRequestSuccessType: type,
        } as ContentPaginationData;

        if (isFavoritesPage()) {
            if (prevFavoriteContentList !== favoriteContentList) {
                paginationData = { ...paginationData, offset: 0 };
            }
        }

        return {
            type,
            requestData: {
                paginationData,
                personId,
                contentEnrollmentStatus,
                contentIds,
                categoryId,
                listTypeParm,
                term,
                randomizeRecommendations: props.randomizeRecommendations || false,
                locales,
            } as ContentRequest,
        };
    };

    const getContentsRequest = (pageOffset?: number): void => {
        const configuration = getContentConfiguration(pageOffset);
        dispatch(getContentListRequest(configuration.requestData));
    };

    useEffect(() => {
        if (!simplified) {
            window.scrollTo(0, 0);
        }

        if (listSize === 0) {
            getContentsRequest();
        }
    }, []);

    useEffect(() => {
        if (isFavoritesPage() || isListSizeRefresh()) {
            getContentsRequest();
        }
    }, [favoriteContentList, limit]);

    useEffect(() => {
        if (viewType === 'listSearchResultStudentView') {
            getContentsRequest(0);
        }
    }, [localesToFilter]);

    useEffect(() => {
        setList(simplified ? listStudentView.slice(0, limit) : listStudentView);
    }, [listSize]);

    const callback = useCallback(() => {
        if (!simplified) {
            if (listSize < listSizeTotal && !favoriteLoading && !contentLoading) {
                getContentsRequest();
                setShowLoader(true);
            } else {
                setShowLoader(false);
            }
        }
    }, [listSize, favoriteLoading, contentLoading, listSizeTotal, simplified]);

    useBottomScrollListener(callback, { offset: 150 });

    return (
        <>
            <div className="content-section row">
                {list.map((contentStudentView: ContentStudentView) => {
                    if (contentStudentView && contentStudentView.content) {
                        const { content, contentEnrollment } = contentStudentView;
                        return (
                            <div className="col-sm-6" key={content.id}>
                                <Link
                                    className="link-to-content"
                                    onClick={() => logEvent(content.id as number, content.name, viewType, categoryId)}
                                    to={`/${Constants.CONTENTS_RESOURCE_NAME}/${content.id}`}
                                >
                                    <ContentCard content={content} finished={contentService.getContentStatusIsCompleted(contentEnrollment)} />
                                </Link>
                            </div>
                        );
                    }
                })}
            </div>
            <div className="row">{showLoader && <Loader />}</div>
        </>
    );
};

export default ContentSection;
